<!-- Analytics drawer panel — 2/3 desktop width, slides in to the right of the history drawer. -->
<template>
  <div class="analytics-drawer" :class="{ 'is-active': isOpen }">
    <div class="analytics-drawer-content">
      <!-- Sticky header -->
      <div class="analytics-header">
        <h3 class="analytics-title">Analytics</h3>
        <div class="analytics-header-controls">
          <div class="buttons has-addons mb-0">
            <button
              v-for="p in periods"
              :key="p.key"
              class="button is-small"
              :class="{ 'is-active': period === p.key }"
              @click="setPeriod(p.key)"
            >{{ p.label }}</button>
          </div>
          <button class="button is-small ml-3" aria-label="close" @click="$emit('close')">
            <span class="icon is-small">
              <FontAwesomeIcon :icon="faXmark" />
            </span>
          </button>
        </div>
      </div>

      <!-- Loading skeleton -->
      <div v-if="loading" class="analytics-body p-4">
        <div class="kpi-strip mb-5">
          <div class="skeleton-block" style="height: 80px;"></div>
          <div class="skeleton-block" style="height: 80px;"></div>
        </div>
        <div class="skeleton-block mb-5" style="height: 260px;"></div>
        <div class="skeleton-block mb-5" style="height: 200px;"></div>
        <div class="skeleton-block" style="height: 180px;"></div>
      </div>

      <div v-else-if="data" class="analytics-body p-4">
        <!-- KPI strip -->
        <div class="kpi-strip">
          <div class="kpi-card kpi-card--tracked">
            <div class="kpi-label">TRACKED</div>
            <div class="kpi-value"><strong>{{ fmtH(data.summary.event_duration_seconds) }}</strong>h {{ fmtM(data.summary.event_duration_seconds) }}m</div>
            <div class="kpi-caption">across {{ periodDayCount }} days</div>
          </div>
          <div v-if="hasContextData" class="kpi-card kpi-card--context">
            <div class="kpi-label">IN CONTEXT</div>
            <div class="kpi-value"><strong>{{ fmtH(data.summary.context_duration_seconds) }}</strong>h {{ fmtM(data.summary.context_duration_seconds) }}m</div>
            <div class="kpi-caption">context timer{{ hasRunningContext ? ' running' : '' }}</div>
          </div>
        </div>

        <!-- Chart section -->
        <div class="analytics-section mt-5">
          <div class="analytics-section-header">
            <span class="analytics-section-label">TOTAL TIME</span>
            <div v-if="hasContextData" class="analytics-section-controls">
              <div class="buttons has-addons mb-0">
                <button class="button is-small" :class="{ 'is-active': chartMode === 'project' }" @click="chartMode = 'project'">Project</button>
                <button class="button is-small" :class="{ 'is-active': chartMode === 'context' }" @click="chartMode = 'context'">Context</button>
                <button class="button is-small" :class="{ 'is-active': chartMode === 'all' }" @click="chartMode = 'all'">All</button>
              </div>
            </div>
          </div>
          <div class="analytics-chart-container">
            <canvas ref="barCanvas"></canvas>
          </div>
        </div>

        <!-- Distribution donut -->
        <div class="analytics-section mt-5">
          <div class="analytics-section-header">
            <span class="analytics-section-label">DISTRIBUTION</span>
            <div class="analytics-section-controls">
              <div class="buttons has-addons mb-0">
                <button
                  class="button is-small"
                  :class="{ 'is-active': donutMode === 'project' }"
                  @click="donutMode = 'project'"
                >By project</button>
                <button
                  class="button is-small"
                  :class="{ 'is-active': donutMode === 'tag' }"
                  @click="donutMode = 'tag'"
                >By tag</button>
                <button
                  v-if="hasContextData"
                  class="button is-small"
                  :class="{ 'is-active': donutMode === 'context' }"
                  @click="donutMode = 'context'"
                >By context</button>
              </div>
            </div>
          </div>
          <div class="analytics-donut-row">
            <div class="analytics-donut-canvas-wrap">
              <canvas ref="donutCanvas"></canvas>
            </div>
            <div class="analytics-donut-legend">
              <div v-for="item in donutItems.slice(0, 6)" :key="item.name" class="legend-row">
                <span class="legend-swatch" :style="{ background: item.color }"></span>
                <span class="legend-name">{{ item.name }}</span>
                <span class="legend-pct">{{ item.percentage }}%</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Breakdown list -->
        <div class="analytics-section mt-5">
          <div class="analytics-section-header">
            <span class="analytics-section-label">BREAKDOWN</span>
            <div class="analytics-section-controls">
              <div class="buttons has-addons mb-0">
                <button
                  v-for="tab in breakdownTabs"
                  :key="tab"
                  class="button is-small"
                  :class="{ 'is-active': breakdownTab === tab }"
                  @click="breakdownTab = tab"
                >{{ tab }}</button>
              </div>
            </div>
          </div>
          <div class="breakdown-list">
            <div v-for="item in breakdownItems" :key="item.name || item.display_label" class="breakdown-row">
              <div class="breakdown-row-top">
                <div class="breakdown-row-left">
                  <span class="legend-swatch" :style="{ background: item.color }"></span>
                  <span class="breakdown-name">{{ item.name || item.display_label }}</span>
                </div>
                <span class="breakdown-stats">{{ fmtHm(item.seconds) }} · {{ item.percentage }}%</span>
              </div>
              <div class="breakdown-bar-track">
                <div class="breakdown-bar-fill" :style="{ width: item.percentage + '%', background: item.color }"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty hint for sparse data -->
        <div v-if="data.time_series.length > 0 && data.time_series.length < 3 && data.summary.event_duration_seconds > 0" class="analytics-hint mt-4">
          Track 3+ days to see trends
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" class="analytics-body p-4 has-text-centered">
        <p class="is-size-6 mt-6">Unable to load analytics</p>
        <p class="is-size-7 has-text-grey mt-2">Something went wrong. <a @click="fetchAnalytics">Try again</a></p>
      </div>

      <!-- Empty state -->
      <div v-else class="analytics-body p-4 has-text-centered">
        <p class="is-size-6 mt-6">No tracked time in this period</p>
        <p class="is-size-7 has-text-grey mt-2">Start tracking timers to see analytics here.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { useBlocksStore } from '../stores/blocks'
import { useAnalyticsChart } from '../composables/useAnalyticsChart'
import { useDoughnutChart } from '../composables/useDoughnutChart'
import { buildNameColorMap, NO_PROJECT_COLOR, CONTEXT_ACCENT_COLOR } from '../utils/chartColors'

const props = defineProps({ isOpen: Boolean })
defineEmits(['close'])

const store = useBlocksStore()

const periods = [
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '6m', label: '6M' },
  { key: '1y', label: '1Y' },
]

const period = ref('7d')
const chartMode = ref('project')
const donutMode = ref('project')
const breakdownTab = ref('Project')
const loading = ref(false)
const data = ref(null)
const error = ref(false)

const barCanvas = ref(null)
const donutCanvas = ref(null)

const periodDayCount = computed(() => {
  const map = { '7d': 7, '30d': 30, '6m': 183, '1y': 365 }
  return map[period.value] || 7
})

const hasContextData = computed(() => {
  return data.value?.summary?.context_duration_seconds > 0
})

const hasRunningContext = computed(() => !!store.activeContext)

// Stable name→color maps derived from the backend-sorted lists.
// Used by bar chart, donut, and breakdown so all three share the same palette.
const colorMaps = computed(() => {
  if (!data.value) return { project: {}, tag: {}, context: {} }
  const contextItems = (data.value.by_context || [])
    .filter(c => c.display_label)
    .map(c => ({ name: c.display_label }))
  return {
    project: buildNameColorMap(data.value.by_project || []),
    tag: buildNameColorMap(data.value.by_tag || []),
    context: buildNameColorMap(contextItems),
  }
})

// Donut data
const donutItems = computed(() => {
  if (!data.value) return []
  if (donutMode.value === 'context') {
    const map = colorMaps.value.context
    return (data.value.by_context || [])
      .filter(c => c.display_label)
      .map(c => ({
        name: c.display_label,
        seconds: c.seconds,
        percentage: c.percentage,
        color: map[c.display_label] ?? CONTEXT_ACCENT_COLOR,
      }))
  }
  const src = donutMode.value === 'project' ? data.value.by_project : data.value.by_tag
  const map = colorMaps.value[donutMode.value]
  return (src || []).map(item => ({ ...item, color: map[item.name] ?? NO_PROJECT_COLOR }))
})

// Breakdown data
const breakdownTabs = computed(() => {
  const tabs = ['Project', 'Tag']
  if (hasContextData.value) tabs.push('Context')
  return tabs
})

const breakdownItems = computed(() => {
  if (!data.value) return []
  if (breakdownTab.value === 'Project') {
    const map = colorMaps.value.project
    return (data.value.by_project || []).map(item => ({ ...item, color: map[item.name] ?? NO_PROJECT_COLOR }))
  }
  if (breakdownTab.value === 'Tag') {
    const map = colorMaps.value.tag
    return (data.value.by_tag || []).map(item => ({ ...item, color: map[item.name] ?? NO_PROJECT_COLOR }))
  }
  if (breakdownTab.value === 'Context') {
    const map = colorMaps.value.context
    return (data.value.by_context || []).map(c => ({
      ...c,
      name: c.display_label,
      color: map[c.display_label] ?? CONTEXT_ACCENT_COLOR,
    }))
  }
  return []
})

// Formatting helpers
function fmtHm(seconds) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  return `${h}h ${m}m`
}

function fmtH(seconds) { return Math.floor(seconds / 3600) }
function fmtM(seconds) { return Math.floor((seconds % 3600) / 60) }

// Chart composables
useAnalyticsChart(barCanvas, data, chartMode, colorMaps)
useDoughnutChart(donutCanvas, donutItems)

// Fetch data
async function fetchAnalytics() {
  loading.value = true
  error.value = false
  try {
    const { data: result } = await axios.get(`/api/analytics?period=${period.value}`)
    data.value = result
  } catch (err) {
    data.value = null
    error.value = true
  } finally {
    loading.value = false
  }
}

function setPeriod(p) {
  period.value = p
  fetchAnalytics()
}

watch(hasContextData, (has) => {
  if (!has) {
    if (chartMode.value !== 'project') chartMode.value = 'project'
    if (donutMode.value === 'context') donutMode.value = 'project'
  }
})

watch(() => props.isOpen, (open) => {
  if (open) fetchAnalytics()
})
</script>

<style scoped>
.analytics-drawer {
  display: contents;
}

.analytics-drawer-content {
  position: fixed;
  top: 0;
  left: calc(-2 * 100vw / 3);
  width: calc(2 * 100vw / 3);
  height: 100%;
  background: var(--bulma-scheme-main);
  box-shadow: 2px 0 8px rgba(0,0,0,0.12);
  transition: transform 0.3s ease-out;
  overflow-y: auto;
  z-index: 40;
  pointer-events: none;
  visibility: hidden;
}
.analytics-drawer.is-active .analytics-drawer-content {
  transform: translateX(calc(300px + 2 * 100vw / 3));
  pointer-events: auto;
  visibility: visible;
}

/* Header */
.analytics-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--bulma-scheme-main);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--bulma-scheme-main-ter);
}
.analytics-title {
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
  color: var(--bulma-text);
}
.analytics-header-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* KPI strip */
.kpi-strip {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 0.75rem;
}
.kpi-card {
  padding: 0.5rem 1rem;
  border-radius: 0;
}
.kpi-card--tracked {
  border-left: 3px solid #7a90a8;
}
.kpi-card--context {
  border-left: 3px solid #b8896e;
}
.kpi-label {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: var(--bulma-text-weak);
  margin-bottom: 0.25rem;
}
.kpi-value {
  font-size: 1.6rem;
  font-weight: 300;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  color: var(--bulma-text);
}
.kpi-value :deep(strong) { font-weight: 700; }
.kpi-caption {
  font-size: 0.65rem;
  color: var(--bulma-text-weak);
  margin-top: 0.25rem;
}

/* Section cards — each analytics section sits in a card */
.analytics-section {
  background: var(--bulma-scheme-main-bis);
  border-radius: var(--bulma-radius-large);
  padding: 1rem;
}
.analytics-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.analytics-section-label {
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--bulma-text-weak);
  text-transform: uppercase;
}
.analytics-section-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Context overlay button swatch */
.analytics-context-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: rgba(213, 99, 45, 0.5);
  margin-right: 0.25rem;
}

/* Chart container */
.analytics-chart-container {
  position: relative;
  height: 220px;
}

/* Donut row */
.analytics-donut-row {
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  align-items: flex-start;
  gap: 1.25rem;
}
.analytics-donut-canvas-wrap {
  width: 180px;
  height: 180px;
  flex-shrink: 0;
}
.analytics-donut-legend {
  flex: 1;
  min-width: 0;
}
.legend-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 0.4rem;
  padding: 0.4rem 0;
  font-size: 0.75rem;
  border-bottom: 1px solid var(--bulma-scheme-main-ter);
}
.legend-row:last-child {
  border-bottom: none;
}
.legend-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
}
.legend-name {
  color: var(--bulma-text);
  line-height: 1.3;
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
}
.legend-pct {
  justify-self: end;
  white-space: nowrap;
  color: var(--bulma-text-weak);
  font-variant-numeric: tabular-nums;
}

@media (max-width: 900px) {
  .analytics-donut-row {
    grid-template-columns: 1fr;
  }

  .analytics-donut-canvas-wrap {
    margin: 0 auto;
  }
}

/* Breakdown list */
.breakdown-list {
  display: flex;
  flex-direction: column;
}
.breakdown-row {
  cursor: default;
  padding: 0.5rem 0.25rem;
  border-bottom: 1px solid var(--bulma-scheme-main-ter);
  transition: background 0.1s;
}
.breakdown-row:last-child {
  border-bottom: none;
}
.breakdown-row:hover {
  background: var(--bulma-scheme-main-ter);
  border-radius: var(--bulma-radius);
}
.breakdown-row-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}
.breakdown-row-left {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}
.breakdown-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--bulma-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.breakdown-stats {
  font-size: 0.75rem;
  color: var(--bulma-text-weak);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.breakdown-bar-track {
  height: 4px;
  background: var(--bulma-scheme-main-ter);
  border-radius: 2px;
  overflow: hidden;
}
.breakdown-bar-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

/* Hint */
.analytics-hint {
  text-align: center;
  font-size: 0.7rem;
  color: var(--bulma-text-weak);
  padding: 0.5rem;
}

/* Mobile: full-width drawer, slide in from right */
@media (max-width: 768px) {
  .analytics-drawer-content {
    left: 100vw;
    width: 100vw;
    max-width: 100vw;
  }

  .analytics-drawer.is-active .analytics-drawer-content {
    transform: translateX(-100vw);
  }
}
</style>
